import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  DropdownList,
  type DropdownListGroup,
  type IDropdownListItem,
} from "@/components/ui/dropdown-list/dropdown-list";
import { nodeVariableToGroups, type NodeVariable } from "../types";

interface VariableSelectorProps {
  variables: NodeVariable[];
  type: "string" | "number";
  selectedValue?: {
    node_id?: string;
    field_id?: string;
    output_name?: string;
    global_type?:
      | "APPLICANT"
      | "NODE_EXECUTION_START"
      | "NODE_EXECUTION_FINISH"
      | "CURRENT_TIME";
    value?: string;
  };
  onUpdateElement: (val: {
    node_id?: string;
    field_id?: string;
    output_name?: string;
    value?: string;
    global_type?:
      | "APPLICANT"
      | "NODE_EXECUTION_START"
      | "NODE_EXECUTION_FINISH"
      | "CURRENT_TIME";
  }) => void;
  placeholder?: string;
  group: "right" | "up-right" | "down-right";
  useNameAsValue?: boolean; // When true, uses variable name as value instead of ID
}

export const VariableSelector: React.FC<VariableSelectorProps> = ({
  variables,
  type,
  selectedValue,
  onUpdateElement,
  placeholder = "Elegir variable",
  group = "right",
  useNameAsValue = false,
}) => {
  const [variableSearch, setVariableSearch] = useState("");
  const [_selectedVariable, setSelectedVariable] =
    useState<IDropdownListItem>();
  const [_selectedGroup, setSelectedGroup] = useState<DropdownListGroup>();

  // Create unfiltered groups for lookup (not affected by search)
  const allVariableGroups = useMemo(() => {
    const groups = nodeVariableToGroups(variables);
    const getAllowedTypes = (type: string) => {
      switch (type) {
        case "string":
          return ["string", "number"];
        case "float":
        case "number":
          // For number type, accept number and float
          return ["number", "float"];
        default:
          return ["string", "number"];
      }
    };
    return groups
      .map((group) => ({
        ...group,
        items: group.items
          .filter((item) => {
            // If item.detail (type) is not present, include it
            if (!item.detail) return true;
            return getAllowedTypes(type).includes(item.detail.toLowerCase());
          })
          .map((item) => {
            // If useNameAsValue is true, use the label (variable name) as the value
            if (useNameAsValue) {
              return {
                ...item,
                value: item.label, // Use name as value
              };
            }
            return item; // Keep original value (ID)
          }),
      }))
      .filter((group) => group.items.length > 0);
  }, [variables, type, useNameAsValue]);

  // Create filtered groups for display (affected by search)
  const variableGroups = useMemo(() => {
    if (!variableSearch.trim()) {
      return allVariableGroups;
    }

    const searchLower = variableSearch.toLowerCase();
    return allVariableGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => {
          return (
            (item?.label as string)?.toLowerCase()?.includes(searchLower) ||
            group.groupLabels.some((label) =>
              label.toLowerCase().includes(searchLower),
            )
          );
        }),
      }))
      .filter((group) => group.items.length > 0);
  }, [allVariableGroups, variableSearch]);

  // Sync local state when selectedValue prop changes
  useEffect(() => {
    if (!selectedValue) {
      // Clear selection if selectedValue is empty
      setSelectedVariable(undefined);
      setSelectedGroup(undefined);
      return;
    }
    let foundVariable: IDropdownListItem | undefined;
    let foundGroup: DropdownListGroup | undefined;

    for (const group of allVariableGroups) {
      if (group.groupId === "global-variables" && selectedValue.global_type) {
        const variable = group.items.find((item) => {
          return item.value === selectedValue.value;
        });
        if (variable) {
          foundVariable = variable as IDropdownListItem;
          foundGroup = group as DropdownListGroup;
          break;
        }
        continue;
      }

      if (group.groupId !== selectedValue.node_id) {
        continue;
      }
      const variable = group.items.find((item) => {
        // Determine effective field_id (ignore if it equals node_id, like calculated field nodes)
        const effectiveFieldId =
          selectedValue.field_id &&
          selectedValue.field_id !== selectedValue.node_id
            ? selectedValue.field_id
            : undefined;

        if (effectiveFieldId && selectedValue.output_name) {
          return (
            item.value === `${effectiveFieldId}-${selectedValue.output_name}`
          );
        }

        if (effectiveFieldId) {
          return item.value === effectiveFieldId;
        }

        if (selectedValue.output_name) {
          return (
            item.value ===
            `${selectedValue.node_id}-${selectedValue.output_name}`
          );
        }

        if (selectedValue.global_type) {
          return item.value === selectedValue.output_name;
        }
        return item.value === selectedValue.node_id;
      });

      if (variable) {
        foundVariable = variable as IDropdownListItem;
        foundGroup = group as DropdownListGroup;
        break;
      }
    }

    if (foundVariable && foundGroup) {
      // Use functional updates and check if values changed to prevent unnecessary updates
      setSelectedVariable((prev) => {
        if (prev?.value === foundVariable.value) {
          return prev; // No change needed
        }
        return foundVariable;
      });

      // Update group only if it changed
      setSelectedGroup((prev) => {
        if (prev?.groupId === foundGroup.groupId) {
          return prev; // No change needed
        }
        return foundGroup;
      });
    } else {
      // Clear selection if variable not found
      setSelectedVariable((prev) => {
        if (!prev) return prev; // Already cleared
        return undefined;
      });
      setSelectedGroup((prev) => {
        if (!prev) return prev; // Already cleared
        return undefined;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValue]); // Only depend on selectedValue to prevent infinite loops

  const onVariableSelection = useCallback(
    (variableId: string, groupId: string) => {
      const sourceNode = variables.find((v) => v.nodeId === groupId);

      // Try to find the variable by ID first
      let originalVariable = sourceNode?.variables.find(
        (v) => v.id === variableId,
      );

      // If not found, it might be a composite ID (field_id-output_name)
      // Try to find by matching the composite pattern
      if (!originalVariable && variableId.includes("-")) {
        originalVariable = sourceNode?.variables.find((v) => {
          if (v.field_id && v.output_name) {
            return `${v.field_id}-${v.output_name}` === variableId;
          }
          return false;
        });
      }

      const globalType = originalVariable?.global_type;

      if (globalType) {
        // Variable global: usar value con nodeId y global_type
        // Para NODE_EXECUTION_START/FINISH: value = nodeId del nodo a monitorear
        // Para APPLICANT: value = nombre de la propiedad (usamos el nombre de la variable)
        const globalValue =
          globalType === "APPLICANT" || globalType === "CURRENT_TIME"
            ? originalVariable?.id || ""
            : originalVariable?.node_id || groupId;

        onUpdateElement({
          value: globalValue,
          global_type: globalType || "APPLICANT",
        });
      } else {
        // For non-global variables, always include field_id (which is the variable ID)
        const result: {
          node_id: string;
          field_id?: string;
          output_name?: string;
        } = {
          node_id: originalVariable?.node_id || groupId,
        };

        if (originalVariable?.field_id && originalVariable?.output_name) {
          // Expanded data center variable
          result.field_id = originalVariable.field_id;
          result.output_name = originalVariable.output_name;
        } else if (originalVariable?.output_name) {
          // Variable with output_name (like interoperability)
          result.output_name = originalVariable.output_name;
        } else {
          // Regular variable - use the variable ID as field_id
          // BUT: if the variable ID equals the node_id (like calculated field nodes),
          // don't send field_id because the backend will reject it
          const varId = originalVariable?.id || variableId;
          const nodeId = originalVariable?.node_id || groupId;
          if (varId !== nodeId) {
            result.field_id = varId;
          }
        }

        onUpdateElement(result);
      }
    },
    [onUpdateElement, variables],
  );

  // Memoize the toggleSelection callback to prevent recreations
  const handleToggleSelection = useCallback(
    (variable: IDropdownListItem, group?: DropdownListGroup) => {
      onVariableSelection(variable.value, group?.groupId || "");
    },
    [onVariableSelection],
  );

  // Memoize the search callback
  const handleSearch = useCallback((query: string) => {
    setVariableSearch(query);
  }, []);

  return (
    <DropdownList
      group={group}
      options={variableGroups as DropdownListGroup[]}
      selectedValue={_selectedVariable}
      selectedGroup={_selectedGroup}
      toggleSelection={handleToggleSelection}
      widthClassName="flex-1"
      placeholder={placeholder}
      search={{
        placeholder: "Buscar variable",
        onSearch: handleSearch,
        query: variableSearch,
      }}
    />
  );
};
